import { useMsal } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { IconButton, Spinner, Typography } from '@volvo/vcdk-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IWifiNetworkRow } from '@/service/vpc/types/IWifiConfiguration';
import type { ColumnDef } from '@tanstack/react-table';

import { withComponentErrorBoundary } from '@/components/ComponentErrorBoundary';
import FormatDateTime from '@/components/FormatDateTime';
import AddNetworkModal from '@/components/modals/AddNetworkModal';
import DeleteNetworkModal from '@/components/modals/DeleteNetworkModal';
import EditNetworkModal from '@/components/modals/EditNetworkModal';
import WifiErrorLogModal from '@/components/modals/WifiErrorLogModal';
import WifiPollingModal from '@/components/modals/WifiPollingModal';
import Table from '@/components/Table';
import WifiConnectionStatus from '@/components/WifiConnectionStatus';
import { useWifiConfigPolling } from '@/hooks/useWifiConfigPolling';
import NetworkRowActions from '@/routes/_authenticated/_account/fleets/$fleetId/units/$endCustomerUnitId/settings/_components/NetworkRowActions';
import { getWifiConfigurationQueryOptions, unitByIdQueryOptions } from '@/service/vpc/queryOptions';
import { telemetry } from '@/utils';
import { retrieveEndCustomerId } from '@/utils/account';

const NetworkSettings: React.FC = () => {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const { endCustomerUnitId } = useParams({
    strict: false,
  });

  const { data } = useQuery(unitByIdQueryOptions({ endCustomerUnitId: endCustomerUnitId! }));
  const chassisId = telemetry.getTelemetryDeviceChassisId(data?.telemetryDeviceInfo, 'TGW');

  const activeAccount = instance.getActiveAccount();
  const endCustomerId = activeAccount ? retrieveEndCustomerId(activeAccount) : undefined;
  const isOwner = endCustomerId != null && String(data?.ownedByEndCustomerId) === endCustomerId;
  const isManager = endCustomerId != null && String(data?.unitManagerId) === endCustomerId;
  const canEdit = isOwner || isManager;

  const { data: wifiData } = useQuery(getWifiConfigurationQueryOptions({ chassisId }));

  const { status: pollingStatus, isPolling, startPolling, reset: resetPolling } = useWifiConfigPolling({ chassisId });

  const [editRow, setEditRow] = useState<IWifiNetworkRow | null>(null);
  const [deleteRow, setDeleteRow] = useState<IWifiNetworkRow | null>(null);
  const [showErrorLog, setShowErrorLog] = useState(false);

  // Trigger polling on mount when chassisId is available
  useEffect(() => {
    if (chassisId) {
      void startPolling();
    }
  }, [chassisId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = () => {
    void startPolling();
  };

  const handlePollingModalClose = () => {
    resetPolling();
  };

  const handleMutationSuccess = (correlationId: string, updatedNetworks: IWifiNetworkRow[]) => {
    void startPolling(correlationId, updatedNetworks);
  };

  const columns = [
    {
      accessorKey: 'priority',
      id: 'priority',
      header: t(($) => $.components.table.network_settings_table.priority),
      cell: (info) => (
        <Typography variant={'body'} className="text-subtle">
          {info.getValue<number>()}
        </Typography>
      ),
    },
    {
      accessorKey: 'ssid',
      id: 'ssid',
      header: t(($) => $.components.table.network_settings_table.ssid),
      cell: (info) => (
        <Typography variant={'body'} className="text-subtle">
          {info.getValue<string>() ?? '-'}
        </Typography>
      ),
    },
    {
      accessorKey: 'wifiFrequency',
      id: 'Frequency',
      header: t(($) => $.components.table.network_settings_table.frequency),
      cell: (info) => (
        <Typography variant={'body'} className="text-subtle">
          {info.getValue<string>()}
        </Typography>
      ),
    },
    {
      accessorKey: 'receivedDateTime',
      id: 'receivedDateTime',
      header: t(($) => $.components.table.network_settings_table.last_updated),
      cell: (info) => {
        return <FormatDateTime date={info.getValue<string>()} variant="body" type="datetime" />;
      },
    },
    {
      accessorFn: (row) => row.isConnected,
      id: 'Connection',
      header: t(($) => $.components.table.network_settings_table.connection),
      cell: (info) => <WifiConnectionStatus row={info.row.original} />,
    },
    ...(canEdit
      ? [
          {
            accessorKey: 'actions',
            id: 'actions',
            header: '',
            size: 30,
            cell: (info: { row: { original: IWifiNetworkRow } }) => (
              <NetworkRowActions
                row={info.row.original}
                onRefresh={handleRefresh}
                onEdit={setEditRow}
                onDelete={setDeleteRow}
              />
            ),
          },
        ]
      : []),
  ] as ColumnDef<IWifiNetworkRow>[];

  if (!chassisId || data?.mainSegment !== 'Marine') return null;

  return (
    <>
      <Table
        isLoading={false}
        headerTitle={t(($) => $.pages.unit.settings.network_settings.title)}
        data={isPolling ? [] : wifiData || []}
        columns={columns}
        showSearch={false}
        showPagination={false}
        emptyState={{
          title: t(($) => $.pages.unit.settings.network_settings.empty_state.title),
          content: isPolling ? (
            <div className="flex flex-col items-center gap-6 py-4">
              <Spinner size={48} />
              <div className="flex flex-col items-center gap-2 text-center">
                <Typography variant="heading3">
                  {t(($) => $.pages.unit.settings.network_settings.updating_title)}
                </Typography>
                <Typography variant="body" className="text-subtle">
                  {t(($) => $.pages.unit.settings.network_settings.updating_description)}
                </Typography>
              </div>
            </div>
          ) : undefined,
        }}
        actions={
          canEdit ? (
            <>
              <IconButton icon="document-error" size="medium" onClick={() => setShowErrorLog(true)} />
              <AddNetworkModal
                chassisId={chassisId}
                existingNetworks={wifiData || []}
                onMutationSuccess={handleMutationSuccess}
              />
            </>
          ) : undefined
        }
      />
      {canEdit && (
        <EditNetworkModal
          chassisId={chassisId}
          row={editRow}
          existingNetworks={wifiData || []}
          onClose={() => setEditRow(null)}
          onMutationSuccess={handleMutationSuccess}
        />
      )}
      {canEdit && (
        <DeleteNetworkModal
          chassisId={chassisId}
          row={deleteRow}
          existingNetworks={wifiData || []}
          onClose={() => setDeleteRow(null)}
          onMutationSuccess={handleMutationSuccess}
        />
      )}
      <WifiPollingModal isOpen={pollingStatus === 'failure'} status={pollingStatus} onClose={handlePollingModalClose} />
      <WifiErrorLogModal chassisId={chassisId} isOpen={showErrorLog} onClose={() => setShowErrorLog(false)} />
    </>
  );
};

export default withComponentErrorBoundary(NetworkSettings);
